import { titles, type FilmTitle, type TitleKind } from "@/mocks/titles";

export { titles };
export type { FilmTitle, TitleKind };

export const allRegions = ["全部", ...Array.from(new Set(titles.map((t) => t.region)))];
export const allYears = ["全部", "2025", "2024", "2023", "2022", "2021"];
export const allGenres = [
  "全部",
  ...Array.from(new Set(titles.flatMap((t) => t.genres))),
];

export const sortOptions = [
  { value: "latest", label: "上映时间" },
  { value: "rating", label: "评分最高" },
  { value: "popular", label: "热门度" },
] as const;

export type SortValue = (typeof sortOptions)[number]["value"];

export interface TitleFilters {
  year: string;
  region: string;
  genre: string;
  sort: SortValue;
}

export const defaultFilters: TitleFilters = {
  year: "全部",
  region: "全部",
  genre: "全部",
  sort: "latest",
};

export function getTitleById(id: string | undefined): FilmTitle | undefined {
  return titles.find((t) => t.id === id);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万次播放`;
  return `${views} 次播放`;
}

export function formatRating(rating: number): string {
  return rating.toFixed(1);
}

export function filterTitles(
  source: FilmTitle[],
  filters: Partial<TitleFilters>,
): FilmTitle[] {
  const { year = "全部", region = "全部", genre = "全部", sort = "latest" } = filters;
  const result = source.filter((t) => {
    if (year !== "全部" && String(t.year) !== year) return false;
    if (region !== "全部" && t.region !== region) return false;
    if (genre !== "全部" && !t.genres.includes(genre)) return false;
    return true;
  });
  const sorted = [...result];
  if (sort === "latest") sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
  if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
  if (sort === "popular") sorted.sort((a, b) => b.views - a.views);
  return sorted;
}

export function searchTitles(keyword: string): FilmTitle[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return titles.filter((t) => {
    const pool = [
      t.title,
      t.origTitle,
      t.director,
      t.region,
      ...t.genres,
      ...t.cast,
    ]
      .join(" ")
      .toLowerCase();
    return pool.includes(q);
  });
}

export function getRelated(t: FilmTitle, limit = 6): FilmTitle[] {
  return titles
    .filter((x) => x.id !== t.id)
    .map((x) => {
      let score = 0;
      if (x.kind === t.kind) score += 2;
      if (x.director === t.director) score += 4;
      score += x.genres.filter((g) => t.genres.includes(g)).length * 2;
      if (x.region === t.region) score += 1;
      return { item: x, score };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((x) => x.item);
}

export function getTrending(limit = 8): FilmTitle[] {
  return [...titles].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getLatest(limit = 8): FilmTitle[] {
  return [...titles]
    .sort((a, b) => b.year - a.year || b.rating - a.rating)
    .slice(0, limit);
}

/* ---------- 收藏 & 浏览记录（本地存储演示） ---------- */

const FAV_KEY = "cc_favorites";
const HISTORY_KEY = "cc_history";

export function getFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function toggleFavorite(id: string): string[] {
  const list = getFavorites();
  const next = list.includes(id) ? list.filter((x) => x !== id) : [id, ...list];
  try {
    localStorage.setItem(FAV_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota errors */
  }
  return next;
}

export function recordHistory(id: string): void {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list: { id: string; viewedAt: number }[] = Array.isArray(parsed) ? parsed : [];
    const next = [
      { id, viewedAt: Date.now() },
      ...list.filter((x) => x.id !== id),
    ].slice(0, 50);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}